import { MouseEvent, ReactNode } from 'react';
import { sprintf } from 'sprintf-js';

import { ApiUserEnvironmentOrgMini } from 'api/userEnvironment/types/ApiUserEnvironmentOrgMini';
import { Box } from 'components/Box/Box';
import { Button } from 'components/Button/Button';
import { Dropdown } from 'components/Dropdown/Dropdown';
import { closeDropdownFromElementWithin } from 'components/Dropdown/closeDropdownFromElementWithin';
import { DropdownListMenu } from 'components/Dropdown/menus/ListMenu/DropdownListMenu';
import {
  DropdownListMenuItem,
  DropdownListMenuItemRouter,
} from 'components/Dropdown/menus/ListMenu/DropdownListMenuItem';
import {
  DropdownListMenuSection,
  DropdownListMenuSectionTitle,
} from 'components/Dropdown/menus/ListMenu/DropdownListMenuSection';
import { Icon } from 'components/Icon/Icon';
import {
  userDashboardAccountSettingsRoute,
  userDashboardProfessionalExperienceRoute,
  userDashboardProfileRoute,
  userDashboardSavedItemsRoute,
} from 'modules/userDashboard/routing/userDashboard.routes';
import { trackClicked } from 'utils/analytics/track/trackClicked';

import {
  MyAccountMobileDropdownBody,
  MyAccountMobileDropdownFooter,
  MyAccountMobileDropdownHeader,
  MyAccountMobileDropdownHolder,
  MyAccountMobileDropdownLink,
  MyAccountMobileDropdownSection,
  MyAccountMobileDropdownSectionTitle,
  MyDropdownMobileHeaderTitle,
} from './MyAccountDropdown.styled';
import { MyAccountDropdownProfileSection } from './MyAccountDropdownProfileSection';

type Props = {
  trigger: ReactNode;
  displayName: string;
  orgs: ApiUserEnvironmentOrgMini[];
  showProfileLink: boolean;
  promoteResumeDb: boolean;
  showEmploymentProfileLink: boolean;
  hasResume: boolean;
  isLoggedIn: boolean;
  loginLink: string;
  onClickLogin: (
    event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => void;
};

export function MyAccountDropdown({
  trigger,
  displayName,
  orgs,
  showProfileLink,
  promoteResumeDb,
  showEmploymentProfileLink,
  hasResume,
  isLoggedIn,
  loginLink,
  onClickLogin,
}: Props) {
  return (
    <Dropdown trigger={trigger}>
      <DropdownListMenu
        width={240}
        maxHeight="calc(100vh - 80px)"
        qaId="nav-dropdown"
      >
        {promoteResumeDb && (
          <MyAccountDropdownProfileSection hasResume={hasResume} />
        )}

        {orgs.length > 0 && (
          <DropdownListMenuSection>
            <DropdownListMenuSectionTitle>
              {getText('Manage')}
            </DropdownListMenuSectionTitle>

            {orgs.map((org) => (
              <DropdownListMenuItemRouter
                key={org.id}
                to={`/admin/${org.id}`}
                onClick={(event) => {
                  trackClicked('Header Dropdown Link', {
                    link_type: 'Organization',
                    url: `/admin/${org.id}`,
                  });
                  closeDropdownFromElementWithin(event.target as HTMLElement);
                }}
              >
                <span className="truncate">{org.name}</span>
              </DropdownListMenuItemRouter>
            ))}
          </DropdownListMenuSection>
        )}

        <DropdownListMenuSection>
          <DropdownListMenuSectionTitle>
            {getText('My Account')}
          </DropdownListMenuSectionTitle>

          <DropdownListMenuItemRouter
            data-qa-id="page-header-account-dropdown-saved-items"
            to={userDashboardSavedItemsRoute.with({})}
            onClick={(event) => {
              trackClicked('Header Dropdown Link', {
                link_type: 'My Saved Items',
                url: (event.target as HTMLAnchorElement).href,
              });
              closeDropdownFromElementWithin(event.target as HTMLElement);
            }}
          >
            <Icon name="bookmark" size={16} />
            <Box display="inline-block" ml={2}>
              {getText('My Saved Items')}
            </Box>
          </DropdownListMenuItemRouter>

          {showProfileLink && (
            <DropdownListMenuItemRouter
              data-qa-id="page-header-account-dropdown-profile"
              to={userDashboardProfileRoute.with({})}
              onClick={(event) => {
                trackClicked('Header Dropdown Link', {
                  link_type: 'Profile',
                  url: (event.target as HTMLAnchorElement).href,
                });
                closeDropdownFromElementWithin(event.target as HTMLElement);
              }}
            >
              <Icon name="person" size={16} />
              <Box display="inline-block" ml={2}>
                {getText('Profile')}
              </Box>
            </DropdownListMenuItemRouter>
          )}

          {showEmploymentProfileLink && (
            <DropdownListMenuItemRouter
              data-qa-id="page-header-account-dropdown-professional-experience"
              to={userDashboardProfessionalExperienceRoute.with({})}
              onClick={(event) => {
                trackClicked('Header Dropdown Link', {
                  link_type: 'Professional Experience',
                  url: (event.target as HTMLAnchorElement).href,
                });
                closeDropdownFromElementWithin(event.target as HTMLElement);
              }}
            >
              <Icon name="resume" size={16} />
              <Box display="inline-block" ml={2}>
                {getText('Professional Experience')}
              </Box>
            </DropdownListMenuItemRouter>
          )}

          <DropdownListMenuItemRouter
            data-qa-id="page-header-account-dropdown-account-settings"
            to={userDashboardAccountSettingsRoute.with({})}
            onClick={(event) => {
              trackClicked('Header Dropdown Link', {
                link_type: 'Settings',
                url: (event.target as HTMLAnchorElement).href,
              });
              closeDropdownFromElementWithin(event.target as HTMLElement);
            }}
          >
            <Icon name="gear" size={16} />
            <Box display="inline-block" ml={2}>
              {getText('Account Settings')}
            </Box>
          </DropdownListMenuItemRouter>
        </DropdownListMenuSection>

        <DropdownListMenuSection>
          <DropdownListMenuItem
            href="/logout"
            data-qa-id="logout-link"
            $logoutLink
          >
            {getText('Log Out')}
          </DropdownListMenuItem>
        </DropdownListMenuSection>
      </DropdownListMenu>

      <MyAccountMobileDropdownHolder>
        <MyAccountMobileDropdownHeader>
          <MyDropdownMobileHeaderTitle>
            {displayName
              ? /* TRANSLATORS: user greeting at top of page */
                sprintf(getText('Hi, %(name)s!'), {
                  name: displayName,
                })
              : getText('Menu')}
          </MyDropdownMobileHeaderTitle>

          <Button
            type="button"
            onClick={(event) =>
              closeDropdownFromElementWithin(event.target as HTMLElement)
            }
            aria-label={getText('Close search')}
            variant="tertiary-grey"
            icon={{ type: 'normalized', name: 'close' }}
          />
        </MyAccountMobileDropdownHeader>

        <MyAccountMobileDropdownBody data-qa-id="nav-dropdown-mobile">
          <MyAccountMobileDropdownSection>
            <MyAccountMobileDropdownLink
              to={userDashboardSavedItemsRoute.with({})}
              onClick={(event) => {
                closeDropdownFromElementWithin(event.target as HTMLElement);
              }}
            >
              {getText('My Saved Items')}
            </MyAccountMobileDropdownLink>

            {showEmploymentProfileLink && (
              <MyAccountMobileDropdownLink
                to={userDashboardProfessionalExperienceRoute.with({})}
                onClick={(event) => {
                  closeDropdownFromElementWithin(event.target as HTMLElement);
                }}
                data-qa-id="page-header-account-dropdown-professional-experience"
              >
                {getText('Profile')}
              </MyAccountMobileDropdownLink>
            )}

            <MyAccountMobileDropdownLink
              data-qa-id="page-header-account-dropdown-account-settings"
              to="/dashboard/account-settings"
              onClick={(event) => {
                closeDropdownFromElementWithin(event.target as HTMLElement);
              }}
            >
              {getText('Settings')}
            </MyAccountMobileDropdownLink>
          </MyAccountMobileDropdownSection>

          {orgs.length > 0 && (
            <MyAccountMobileDropdownSection>
              <MyAccountMobileDropdownSectionTitle>
                {getText('Manage')}
              </MyAccountMobileDropdownSectionTitle>

              {orgs.map((org) => (
                <div key={org.id}>
                  <MyAccountMobileDropdownLink
                    to={`/admin/${org.id}`}
                    onClick={(event) => {
                      closeDropdownFromElementWithin(
                        event.target as HTMLElement,
                      );
                    }}
                  >
                    {org.name}
                  </MyAccountMobileDropdownLink>
                </div>
              ))}
            </MyAccountMobileDropdownSection>
          )}

          <MyAccountMobileDropdownFooter>
            {isLoggedIn ? (
              <Button
                data-qa-id="logout-button-link"
                type="link"
                href="/logout"
                size="large"
                variant="danger"
                nativeAnchorTag
              >
                {getText('Log Out')}
              </Button>
            ) : (
              <Button
                type="link"
                size="large"
                href={loginLink}
                onClick={(e) => {
                  e.preventDefault();
                  trackClicked('Log In Header');
                  onClickLogin(e);
                }}
                data-qa-id="login-button-link"
                aria-label={getText('Log In')}
                variant="secondary"
              >
                {getText('Log In')}
              </Button>
            )}
          </MyAccountMobileDropdownFooter>
        </MyAccountMobileDropdownBody>
      </MyAccountMobileDropdownHolder>
    </Dropdown>
  );
}
